import React from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ChatEntryPoint } from '@atlaskit/rovo-triggers/chat-entry-point';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { AnyAri } from '@atlassian/ari/any-ari';

import { i18n } from './messages';

const isConfluenceFolderAri = (ari: string | undefined): boolean => {
	if (!ari) {
		return false;
	}

	try {
		const parsedAri = AnyAri.parse(ari);
		return parsedAri.resourceOwner === 'confluence' && parsedAri.resourceType === 'folder';
	} catch {
		return false;
	}
};

export type SummarizeInChatProps = {
	url: string | undefined;
	ari: string | undefined;
	testId?: string;
	onClick?: () => void;
	/**
	 * Spacing for the underlying Atlaskit Button. Defaults to `'compact'` to
	 * preserve existing per-row hover behavior. Pass `'default'` to render the
	 * standard 32px-tall secondary button (e.g. when sitting alongside the
	 * Related links footer toggle).
	 */
	spacing?: 'compact' | 'default';
};

export const SummarizeInChatButton = ({
	url,
	ari,
	testId,
	onClick,
	spacing = 'compact',
}: SummarizeInChatProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');

	const promptRovoChat = (e: React.MouseEvent<HTMLButtonElement>) => {
		onClick?.();
		e.stopPropagation();

		if (!url) {
			return;
		}

		const promptText = isConfluenceFolderAri(ari) ? 'Summarize the contents of ' : 'Summarize ';

		publish({
			type: 'browser-context-payload',
			source: 'search-result',
			data: {
				context: {
					browserUrl: url,
				},
			},
		});
		publish({
			type: 'chat-new',
			source: 'search-result',
			data: {
				dialogues: [],
				// Match pasted Smart Links instead of letting Markdown turn video URLs into embeds.
				prompt: fg('rovo_fps_summarize_inline_card')
					? {
							version: 1,
							type: 'doc',
							content: [
								{
									type: 'paragraph',
									content: [
										{ type: 'text', text: promptText },
										{ type: 'inlineCard', attrs: { url } },
									],
								},
							],
						}
					: `${promptText}${url}`,
				name: `${promptText}${url}`,
				chatEntryPoint: ChatEntryPoint.SEARCH_PAGE_SUMMARIZE_IN_CHAT,
				...(ari && fg('read_fe_search_artifact_context')
					? {
							searchArtifact: {
								url,
								ari,
							},
						}
					: {}),
			},
		});
	};

	return (
		<Button iconBefore={RovoChatIcon} spacing={spacing} onClick={promptRovoChat} testId={testId}>
			{formatMessage(i18n.summarizeInChatButtonText)}
		</Button>
	);
};
